import type { DisplayInfo, EventInfo, EventWindow, RegionEvents } from "./api";
import { prettyEventName } from "./format";

export type EventState = "live" | "upcoming" | "recent" | "later" | "past";

export interface RegionalWindows {
  region: string;
  event_id: string;
  windows: EventWindow[];
}

/** One logical tournament, with its regional variants folded together. */
export interface GroupedEvent {
  base_id: string;
  title: string;
  subtitle: string;
  display: DisplayInfo | null;
  regions: RegionalWindows[];
  state: EventState;
  /** begin of the window that decided `state` (live start or next start) */
  pivot_time: number;
}

/** Preferred display order for region tabs/switchers. Anything not listed
 *  sorts after these, alphabetically. */
export const REGION_ORDER = ["NAC", "EU", "NAW", "BR"];

export function sortRegions(regions: Iterable<string>): string[] {
  const rank = (r: string) => {
    const i = REGION_ORDER.indexOf(r);
    return i === -1 ? REGION_ORDER.length : i;
  };
  return [...new Set(regions)].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

const REGION_SUFFIX = /_(NAC|NAW|EU|BR|ASIA|OCE|ME)$/i;

export function baseEventId(eventId: string): string {
  return eventId.replace(REGION_SUFFIX, "");
}

/** A game can start in the final minute of a window and run ~25 min past the
 *  official end, so a window stays "live" for a grace tail after `end_time` —
 *  otherwise the dashboard drops still-running matches (and stops polling
 *  them) the instant the clock hits end, even though the server keeps parsing
 *  for ~1h. Kept comfortably under the server's poll grace. */
const LIVE_TAIL_MS = 30 * 60_000;

export function windowState(w: EventWindow, now: number): EventState {
  const begin = Date.parse(w.begin_time);
  const end = Date.parse(w.end_time);
  if (Number.isNaN(begin) || Number.isNaN(end)) return "later";
  if (now >= begin && now <= end + LIVE_TAIL_MS) return "live";
  if (now < begin) return "upcoming";
  return "past";
}

const DAY = 86_400_000;

export function groupEvents(regions: RegionEvents[], now: number): GroupedEvent[] {
  const byBase = new Map<string, GroupedEvent>();

  for (const { region, events } of regions) {
    for (const event of events) {
      const base = baseEventId(event.event_id);
      let group = byBase.get(base);
      if (!group) {
        group = {
          base_id: base,
          title: eventTitle(event),
          subtitle: event.display?.title_line_2 ?? "",
          display: event.display,
          regions: [],
          state: "later",
          pivot_time: Number.MAX_SAFE_INTEGER,
        };
        byBase.set(base, group);
      }
      if (!group.display && event.display) {
        group.display = event.display;
        group.title = eventTitle(event);
      }
      group.regions.push({ region, event_id: event.event_id, windows: event.windows });
    }
  }

  for (const group of byBase.values()) {
    const all = group.regions.flatMap(r => r.windows);
    const begins = all.map(w => Date.parse(w.begin_time)).filter(t => !Number.isNaN(t));
    const ends = all.map(w => Date.parse(w.end_time)).filter(t => !Number.isNaN(t));

    if (all.some(w => windowState(w, now) === "live")) {
      group.state = "live";
      group.pivot_time = Math.min(...begins.filter(b => b <= now));
      continue;
    }
    const nextBegin = Math.min(...begins.filter(b => b > now), Number.MAX_SAFE_INTEGER);
    const lastEnd = Math.max(...ends.filter(e => e <= now), 0);

    if (nextBegin !== Number.MAX_SAFE_INTEGER) {
      group.state = nextBegin - now <= 14 * DAY ? "upcoming" : "later";
      group.pivot_time = nextBegin;
    } else if (lastEnd > 0) {
      group.state = now - lastEnd <= 14 * DAY ? "recent" : "past";
      group.pivot_time = lastEnd;
    }
  }

  return [...byBase.values()];
}

/** A group's state seen from ONE region's windows (null = no variant there). */
export function regionalState(
  group: GroupedEvent,
  region: string,
  now: number,
): EventState | null {
  const rv = group.regions.find(r => r.region === region);
  if (!rv || rv.windows.length === 0) return null;
  if (rv.windows.some(w => windowState(w, now) === "live")) return "live";

  const begins = rv.windows.map(w => Date.parse(w.begin_time)).filter(t => !Number.isNaN(t));
  const ends = rv.windows.map(w => Date.parse(w.end_time)).filter(t => !Number.isNaN(t));
  const nextBegin = Math.min(...begins.filter(b => b > now), Number.MAX_SAFE_INTEGER);
  const lastEnd = Math.max(...ends.filter(e => e <= now), 0);

  if (nextBegin !== Number.MAX_SAFE_INTEGER) {
    return nextBegin - now <= 14 * DAY ? "upcoming" : "later";
  }
  if (lastEnd > 0) return now - lastEnd <= 14 * DAY ? "recent" : "past";
  return null;
}

export type EventTier = "featured" | "other";

/** Substrings (matched case-insensitively against the event id + title) that
 *  mark a low-priority event. Deliberately conservative — Android-only mobile
 *  cups are already dropped server-side, so this only demotes the handful of
 *  non-competitive Windows events that slip through. Every token here must
 *  NEVER appear in a real competitive event: a false demote silently buries a
 *  tournament that matters, which is the one thing we can't do. That's why
 *  e.g. "creative" / "build battle" are intentionally absent — Creative,
 *  Zero Build, and Build Battle can all be legitimate competitive formats.
 *  Tune this list as genuinely-junk events show up. */
const OTHER_TOKENS = [
  "mobile", // belt-and-suspenders; mobile is dropped at the source already
  "test cup",
  "testcup",
  "mini venture", // casual reload LTM, never a competitive format
  "miniventure",
  "ranked cup", // open ranked-ladder cups (Solo/Duos/ZB/Reload) — not pro comp
  "rankedcup",
];

/** Featured (the front page) vs other (tucked into a collapsed section). */
export function eventTier(group: GroupedEvent): EventTier {
  const hay = `${group.base_id} ${group.title} ${group.subtitle}`.toLowerCase();
  return OTHER_TOKENS.some(t => hay.includes(t)) ? "other" : "featured";
}

export function bucketEvents(groups: GroupedEvent[]) {
  const sorted = {
    live: [] as GroupedEvent[],
    upcoming: [] as GroupedEvent[],
    recent: [] as GroupedEvent[],
    later: [] as GroupedEvent[],
  };
  for (const g of groups) {
    if (g.state === "live") sorted.live.push(g);
    else if (g.state === "upcoming") sorted.upcoming.push(g);
    else if (g.state === "recent") sorted.recent.push(g);
    else sorted.later.push(g);
  }
  sorted.live.sort((a, b) => a.pivot_time - b.pivot_time);
  sorted.upcoming.sort((a, b) => a.pivot_time - b.pivot_time); // soonest first
  sorted.recent.sort((a, b) => b.pivot_time - a.pivot_time); // freshest first
  sorted.later.sort((a, b) => a.pivot_time - b.pivot_time);
  return sorted;
}

export function eventTitle(event: EventInfo): string {
  const d = event.display;
  if (d?.title_line_1) {
    return [d.title_line_1, d.title_line_2].filter(Boolean).join(" ");
  }
  return prettyEventName(event.event_id);
}

/** "977EA5" → "#977EA5"; falls back when empty/garbage */
export function brandColor(display: DisplayInfo | null, fallback = "#333333"): string {
  const c = display?.primary_color?.trim();
  return c && /^[0-9a-f]{6}$/i.test(c) ? `#${c}` : fallback;
}
